import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Navigation } from "./components/Navigation";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ChatWidget } from "./components/ChatWidget";
import { SwipeableLayout } from "./components/SwipeableLayout";
import { ReferralNotificationListener } from "./components/ReferralNotificationListener";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useSessionSecurity } from "./hooks/useSessionSecurity";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEffect, lazy, Suspense } from "react";
import { useKonamiCode } from "@/hooks/useKonamiCode";
// Lazy load all pages for optimal performance and reduced main-thread work
const Home = lazy(() => import("./pages/Home"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RecordPage = lazy(() => import("./pages/RecordPage"));
// LongRecordPage deprecated - now redirects to RecordPage
const AccountPage = lazy(() => import("./pages/AccountPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const BuyCreditsPage = lazy(() => import("./pages/BuyCreditsPage"));
const TransactionHistory = lazy(() => import("./pages/TransactionHistory"));
const PasswordResetPage = lazy(() => import("./pages/PasswordResetPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const EditNotePage = lazy(() => import("./pages/EditNotePage"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const SupportCategoryPage = lazy(() => import("./pages/SupportCategoryPage"));
const SupportArticlePage = lazy(() => import("./pages/SupportArticlePage"));

// Loading fallback component
import { AppLoader } from "./components/ui/AppLoader";
const PageLoader = () => <AppLoader />;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,    // Prevent jarring refetches on tab switch
    },
  },
});

const App = () => {
  const { toast } = useToast();
  useSessionSecurity();
  useKonamiCode();

  useEffect(() => {
    const checkAndCompleteReferral = async (accessToken?: string) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        console.log('[REFERRAL] Checking referrals...', {
          userId: user?.id,
          emailConfirmed: user?.email_confirmed_at,
          metadata: user?.user_metadata
        });

        if (!user || !user.email_confirmed_at) {
          console.log('[REFERRAL] User not verified');
          return;
        }

        // Resolve token for authenticated calls
        let token = accessToken;
        if (!token) {
          const { data: { session } } = await supabase.auth.getSession();
          token = session?.access_token;
        }

        if (!token) {
          console.log('[REFERRAL] No session token available');
          return;
        }

        const authHeaders = { Authorization: `Bearer ${token}` };

        // Check if referral already exists
        const { data: existingReferral } = await supabase
          .from('referrals')
          .select('*')
          .eq('referee_id', user.id)
          .maybeSingle();

        console.log('[REFERRAL] Existing referral:', existingReferral);

        // If no referral exists but we have validation data in metadata, create it
        if (!existingReferral && user.user_metadata?.referral_validation) {
          const validation = user.user_metadata.referral_validation;
          const referralCode = user.user_metadata.referral_code;

          console.log('[REFERRAL] Creating referral from metadata:', validation);

          const createBody: any = {
            action: 'create',
            referral_code: referralCode,
          };

          // Add appropriate parameters based on code type
          if (validation.is_custom_code) {
            createBody.is_custom_code = true;
            createBody.custom_code_id = validation.custom_code_id;
            createBody.custom_referrer_credits = validation.referrer_credits;
            createBody.custom_referee_credits = validation.referee_credits;
            createBody.referrer_id = null; // Custom codes don't have referrer_id
          } else {
            createBody.referrer_id = validation.referrer_id;
            createBody.is_custom_code = false;
          }

          const { data: createResult, error: createError } = await supabase.functions.invoke(
            'process-referral',
            { body: createBody, headers: authHeaders }
          );

          console.log('[REFERRAL] Create from metadata result:', { createResult, createError });

          if (createResult?.success) {
            // Now complete it, including referral_code in payload
            const { data: completeResult, error: completeError } = await supabase.functions.invoke(
              'process-referral',
              { body: { action: 'complete', referral_code: referralCode || undefined }, headers: authHeaders }
            );

            console.log('[REFERRAL] Complete result:', { completeResult, completeError });

            if (completeResult?.success) {
              toast({
                title: "🎉 Referral Bonus Claimed!",
                description: `You received ${completeResult.referee_credits} bonus credits! Check your email for details.`,
                duration: 8000,
              });
            } else if (completeError) {
              toast({
                variant: "destructive",
                title: "Failed to claim referral bonus",
                description: "Please try again or contact support if the issue persists.",
                duration: 8000,
              });
            }
          }
        }
        // If referral exists and is pending, complete it
        else if (existingReferral && existingReferral.status === 'pending') {
          console.log('[REFERRAL] Completing existing pending referral');

          const { data: result, error: completeError } = await supabase.functions.invoke(
            'process-referral',
            { body: { action: 'complete', referral_code: existingReferral.referral_code }, headers: authHeaders }
          );

          console.log('[REFERRAL] Completion result:', { result, completeError });

          if (result?.success) {
            toast({
              title: "🎉 Referral Bonus Claimed!",
              description: `You received ${result.referee_credits} bonus credits! Check your email for details.`,
              duration: 8000,
            });
          } else if (completeError) {
            toast({
              variant: "destructive",
              title: "Failed to claim referral bonus",
              description: "Please try again or contact support if the issue persists.",
              duration: 8000,
            });
          }
        } else {
          console.log('[REFERRAL] No pending referrals or already credited');
        }
      } catch (error) {
        console.error('[REFERRAL] Error:', error);
      }
    };

    // Run on mount for already-verified users
    checkAndCompleteReferral();

    // Also run whenever auth state changes (e.g., after email verification redirect)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        checkAndCompleteReferral(session?.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, [toast]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SubscriptionProvider>
          <TooltipProvider>
            <ReferralNotificationListener />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ChatWidget />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<><Navigation /><Home /></>} />
                  <Route path="/app" element={<SwipeableLayout><Dashboard /></SwipeableLayout>} />
                  <Route path="/faq" element={<><Navigation /><FAQ /></>} />
                  <Route path="/terms" element={<><Navigation /><TermsOfService /></>} />
                  <Route path="/privacy" element={<><Navigation /><PrivacyPolicy /></>} />
                  <Route path="/support" element={<><Navigation /><SupportPage /></>} />
                  <Route path="/support/:category" element={<><Navigation /><SupportCategoryPage /></>} />
                  <Route path="/support/:category/:slug" element={<><Navigation /><SupportArticlePage /></>} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/app/record" element={<SwipeableLayout><RecordPage /></SwipeableLayout>} />
                  <Route path="/app/record/long" element={<Navigate to="/app/record" replace />} />
                  <Route path="/app/history" element={<SwipeableLayout><HistoryPage /></SwipeableLayout>} />
                  <Route path="/app/buy-credits" element={<BuyCreditsPage />} />
                  <Route path="/app/templates" element={<SwipeableLayout><TemplatesPage /></SwipeableLayout>} />
                  <Route path="/app/account" element={<SwipeableLayout><AccountPage /></SwipeableLayout>} />
                  <Route path="/app/subscription" element={<SubscriptionPage />} />
                  <Route path="/app/subscription/success" element={<SubscriptionSuccessPage />} />
                  <Route path="/app/transactions" element={<TransactionHistory />} />
                  <Route path="/app/admin" element={<SwipeableLayout><AdminDashboard /></SwipeableLayout>} />
                  <Route path="/app/note/:lectureId/edit" element={<EditNotePage />} />
                  <Route path="/reset-password" element={<PasswordResetPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </SubscriptionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
